<script setup>
import { ref } from 'vue';

// 1. Accept user object as a prop
const props = defineProps({
  user: {
    type: Object,
    required: true,
    default: () => ({})
  }
});

// 2. Define emit for logout action
const emit = defineEmits(['logout']);

// 3. Local minimised toggle state & animation state
const isMinimised = ref(false);
const isAnimating = ref(false);

const toggleMinimise = () => {
  if (isAnimating.value) return; // Prevent spam clicking during animation
  
  isAnimating.value = true;
  
  // Mid-way point: change layout structure when card is fully shrunk
  setTimeout(() => {
    isMinimised.value = !isMinimised.value;
  }, 150);

  // Reset animation class when complete
  setTimeout(() => {
    isAnimating.value = false;
  }, 300);
};

const onLogoutClick = () => {
  emit('logout');
};
</script>

<template>
  <div 
    class="card-user-container" 
    :class="{ 
      'is-minimised': isMinimised,
      'is-morphing': isAnimating 
    }"
  >
    
    <!-- Minimise / Expand Toggle Button -->
    <button 
      type="button" 
      class="toggle-btn" 
      @click="toggleMinimise"
      :title="isMinimised ? 'Expand Card' : 'Minimise Card'"
    >
      <i class="fa fa-chevron-up icon-chevron"></i>
    </button>

    <!-- User Avatar Image -->
    <div class="card-user-avatar">
      <img :src="user.picture" alt="User avatar" class="user-image" />
    </div>

    <!-- User Info (Hidden when minimised) -->
    <div v-if="!isMinimised" class="card-user-bio">
      <h4>{{ user.meta_firstname }} {{ user.meta_lastname }}</h4>
      <p>{{ user.meta_role }}</p>
      
      <span v-if="user.meta_location" class="location">
        <span class="location-icon fa fa-map-marker"></span>
        <span v-if="user.meta_location.address_line1" class="location-text">
          {{ user.meta_location.address_line1 }}
        </span>
        <span v-if="user.meta_location.address_line2" class="location-text">
          {{ user.meta_location.address_line2 }}
        </span>
        <span v-if="user.meta_location.address_line3" class="location-text">
          {{ user.meta_location.address_line3 }}
        </span>
        <span v-if="user.meta_location.postcode" class="location-text">
          {{ user.meta_location.postcode }}
        </span>
      </span>
    </div>

    <!-- Logout Button -->
    <div class="card-user-button">
      <a class="btn" @click="onLogoutClick">LOGOUT</a>
    </div>

  </div>
</template>

<style scoped>
/* 1. BASE CONTAINER */
.card-user-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  transform-origin: center;
  will-change: transform, opacity;
}

/* 2. TOGGLE BUTTON & CHEVRON */
.toggle-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 0.85rem;
  border-radius: 4px;
  z-index: 2;
}

.toggle-btn:hover {
  color: #0f172a;
}

.icon-chevron {
  transition: transform 0.3s ease;
}

/* 3. MINIMISED HORIZONTAL LAYOUT */
.card-user-container.is-minimised {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
}

.card-user-container.is-minimised .icon-chevron {
  transform: rotate(180deg);
}

.card-user-container.is-minimised .card-user-avatar,
.card-user-container.is-minimised .card-user-button {
  margin: 0;
}

/* 4. MORPH ANIMATION (Scale down -> swap layout -> scale up) */
.card-user-container.is-morphing {
  animation: cardMorph 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes cardMorph {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.2;
    transform: scale(0.92); /* Briefly shrinks card down while layout switches */
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>